import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authApi, saveAuth } from "../api/auth";
import "../styles/auth.css";
import useGoogleCallback from "../hooks/useGoogleCallback";
import { readNext, stashNext, withNextFrom } from "../lib/nextUrl";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const ERROR_MESSAGES = {
  "Invalid credentials": "Email or password is incorrect. Please try again.",
  "User not found":
    "No account found with this email. Would you like to register?",
  "Network Error":
    "Connection failed. Please check your internet and try again.",
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useGoogleCallback();
  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const getFriendlyError = (msg) =>
    ERROR_MESSAGES[msg] || msg || "Something went wrong. Please try again.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.login({
        email: form.email,
        password: form.password,
      });
      saveAuth(res.token, res.user);
      // Where to go back to rides in ?next=, validated on the way out. replace,
      // so back does not return to the login form they have just cleared.
      navigate(readNext(location.search), { replace: true });
    } catch (err) {
      setError(getFriendlyError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // The backend's Google callback returns to a URL it builds itself and
    // echoes nothing of ours, so ?next= cannot survive the round-trip. Park it
    // for the return leg: see lib/nextUrl.
    stashNext(readNext(location.search));
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f2efe8" }}>
      <Navbar />
      <div className="auth-page">
        <div className="auth-card">
        <Link to="/" className="logo">
          <span className="auth-logo-text">
            ScriptedCAD
          </span>
        </Link>

        <h1 className="auth-heading">Welcome back</h1>
        <p className="auth-sub">Sign in to continue your learning</p>

        {/* Google SSO */}
        <button
          className="auth-sso-btn"
          onClick={handleGoogleLogin}
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
              fill="#4285F4"
            />
            <path
              d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
              fill="#34A853"
            />
            <path
              d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
              fill="#FBBC05"
            />
            <path
              d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"
              fill="#EA4335"
            />
          </svg>
          Continue with Google
        </button>

        <div className="auth-divider">
          <span>or</span>
        </div>

        {error && (
          <div className="auth-error">
            {error}
            {error.includes("No account") && (
              <Link to={withNextFrom(location.search, "/register")} className="auth-error-link">
                {" "}
                Create one →
              </Link>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
              autoFocus
            />
          </div>
          <div className="auth-field">
            <label>
              Password
              <Link to="/forgot-password" className="auth-forgot">
                Forgot password?
              </Link>
            </label>
            <div className="auth-input-wrap">
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={update("password")}
                placeholder="Your password"
              />
              <button
                type="button"
                className="auth-show-pw"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <button type="submit" className="auth-btn" disabled={loading}>
            {loading && <span className="auth-spinner" />}
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <div className="auth-switch">
          Don't have an account?{" "}
          <Link to={withNextFrom(location.search, "/register")}>Create one →</Link>
        </div>
        <div className="auth-switch" style={{ marginTop: ".5rem" }}>
          <Link to="/">← Back to home</Link>
        </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;
